import type { MeasureId } from './measures.model';

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export type MeasureValues = ReadonlyMap<MeasureId, number | number[]>;

/**
 * Computes all statistical measures for the given dataset.
 * Sorts data once and reuses derived values to avoid redundant work.
 */
export function computeAllMeasures(data: number[]): MeasureValues {
  const n = data.length;
  if (n === 0) {
    return new Map();
  }

  const sorted = [...data].sort((a, b) => a - b);
  const min = sorted[0];
  const max = sorted[n - 1];
  const mean = computeMean(data);
  const median = computeMedian(sorted);
  const mode = computeMode(data);
  const q1 = percentile(sorted, 0.25);
  const q3 = percentile(sorted, 0.75);
  const variance = computeVariance(data, mean);
  const stddev = Math.sqrt(variance);
  const skewness = stddev > 0 ? computeSkewness(data, mean, stddev) : 0;

  const result = new Map<MeasureId, number | number[]>();
  result.set('count', n);
  result.set('min', min);
  result.set('max', max);
  result.set('range', max - min);
  result.set('mean', mean);
  result.set('median', median);
  result.set('mode', mode);
  result.set('q1', q1);
  result.set('q3', q3);
  result.set('iqr', q3 - q1);
  result.set('variance', variance);
  result.set('stddev', stddev);
  result.set('skewness', skewness);

  return result;
}

// ---------------------------------------------------------------------------
// Individual computations
// ---------------------------------------------------------------------------

function computeMean(data: number[]): number {
  let sum = 0;
  for (const v of data) {
    sum += v;
  }
  return sum / data.length;
}

function computeMedian(sorted: number[]): number {
  const n = sorted.length;
  const mid = Math.floor(n / 2);
  return n % 2 === 1 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

function computeMode(data: number[]): number[] {
  const freq = new Map<number, number>();
  let maxFreq = 0;

  for (const v of data) {
    const count = (freq.get(v) ?? 0) + 1;
    freq.set(v, count);
    if (count > maxFreq) {
      maxFreq = count;
    }
  }

  // If every value appears equally often, there is no mode
  if (maxFreq === 1) {
    return [];
  }

  const modes: number[] = [];
  for (const [value, count] of freq) {
    if (count === maxFreq) {
      modes.push(value);
    }
  }
  return modes.sort((a, b) => a - b);
}

function computeVariance(data: number[], mean: number): number {
  let sumSq = 0;
  for (const v of data) {
    const d = v - mean;
    sumSq += d * d;
  }
  return sumSq / data.length;
}

function computeSkewness(data: number[], mean: number, stddev: number): number {
  let sumCubed = 0;
  for (const v of data) {
    const d = (v - mean) / stddev;
    sumCubed += d * d * d;
  }
  return sumCubed / data.length;
}

/**
 * Linear interpolation percentile (same method as IBDP).
 * p is between 0 and 1.
 */
function percentile(sorted: number[], p: number): number {
  const n = sorted.length;
  const index = p * (n - 1);
  const lower = Math.floor(index);
  const upper = Math.ceil(index);

  if (lower === upper) {
    return sorted[lower];
  }

  const frac = index - lower;
  return sorted[lower] * (1 - frac) + sorted[upper] * frac;
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

export function formatMeasureValue(id: MeasureId, value: number | number[] | undefined): string {
  if (value === undefined) {
    return '—';
  }

  if (Array.isArray(value)) {
    // Mode can be multiple values
    if (value.length === 0) {
      return 'No mode';
    }
    return value.map((v) => formatNumber(v)).join(', ');
  }

  return formatNumber(value);
}

function formatNumber(value: number): string {
  if (Number.isInteger(value)) {
    return String(value);
  }
  // Show up to 4 decimal places, remove trailing zeros
  return parseFloat(value.toFixed(4)).toString();
}
